/**
 * KAN-961: хлебные крошки страницы в верхней строке кабинета, после
 * "Организация / Пользователь": ссылки, затем title жирнее, subtitle серым
 * в скобках. Одна строка, всё обрезается многоточием. На телефоне видно
 * только title: промежуточные крошки и subtitle прячутся.
 */
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePageHeaderData, type PageHeaderCrumb } from './pageHeaderContext';

function Separator({ className }: { className?: string }) {
  return (
    <span className={cn('shrink-0 text-gray-300', className)} aria-hidden="true">
      /
    </span>
  );
}

/** Крошки без последней, если она повторяет title и не ссылка. */
function crumbsBeforeTitle(crumbs: PageHeaderCrumb[] | undefined, title: string): PageHeaderCrumb[] {
  if (!crumbs || crumbs.length === 0) return [];
  const last = crumbs[crumbs.length - 1];
  return !last.href && last.label === title ? crumbs.slice(0, -1) : crumbs;
}

interface TopBarPageTitleProps {
  /** Перед заголовком уже есть "Организация / Пользователь": нужен разделитель. */
  withLeadingSeparator?: boolean;
  className?: string;
}

export function TopBarPageTitle({ withLeadingSeparator = true, className }: TopBarPageTitleProps) {
  const data = usePageHeaderData();
  if (!data) return null;

  const { title, subtitle } = data;
  const crumbs = crumbsBeforeTitle(data.breadcrumbs, title);
  const subtitleText = typeof subtitle === 'string' ? subtitle : undefined;

  return (
    <div data-testid="topbar-page-title" className={cn('flex min-w-0 items-center gap-2 text-sm', className)}>
      {withLeadingSeparator && <Separator />}
      {crumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="hidden min-w-0 shrink items-center gap-2 sm:flex">
          {crumbs.map((item, index) => (
            <span key={`${item.label}-${index}`} className="flex min-w-0 shrink items-center gap-2">
              {item.href ? (
                <Link
                  to={item.href}
                  className="truncate text-gray-500 transition-colors hover:text-gray-800"
                  data-testid={item['data-testid']}
                >
                  {item.label}
                </Link>
              ) : (
                <span className="truncate text-gray-500" data-testid={item['data-testid']}>
                  {item.label}
                </span>
              )}
              <Separator />
            </span>
          ))}
        </nav>
      )}
      <span className="flex min-w-0 items-baseline gap-1.5" title={subtitleText ? `${title} (${subtitleText})` : title}>
        <h1 className="min-w-0 truncate font-semibold text-gray-900" data-testid="topbar-page-title-text">
          {title}
        </h1>
        {subtitle && (
          <span className="hidden min-w-0 shrink-[4] truncate text-gray-400 sm:block" data-testid="topbar-page-subtitle">
            ({subtitle})
          </span>
        )}
      </span>
    </div>
  );
}
